import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Expand } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { FadeIn, SectionHeader, EASE } from "./Reveal";
import { GALLERY, GALLERY_CATEGORIES } from "../../lib/images";
import { api, API } from "../../lib/api";
import BeforeAfter from "./BeforeAfter";

export default function Gallery() {
    const [filter, setFilter] = useState("Wszystkie");
    const [selected, setSelected] = useState(null);
    const [dynamic, setDynamic] = useState([]);
    const [showStock, setShowStock] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/gallery");
                setDynamic(
                    (data.items || []).map((it, i) => ({
                        id: it.id,
                        cat: it.cat,
                        src: `${API}/gallery/file/${it.storage_path}`,
                        title: it.title,
                        aspect: ["aspect-[4/5]", "aspect-[3/4]", "aspect-square"][i % 3],
                    }))
                );
                setShowStock(data.show_stock !== false);
            } catch { /* zostają zdjęcia stockowe */ }
        })();
    }, []);

    const all = showStock ? [...dynamic, ...GALLERY] : dynamic;
    const items = filter === "Wszystkie" ? all : all.filter((g) => g.cat === filter);

    return (
        <section id="realizacje" className="bg-mist py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="mb-12 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
                    <SectionHeader index="03" eyebrow="Galeria" title="Realizacje, które mówią same za siebie." />
                    <FadeIn delay={0.15}>
                        <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
                            {GALLERY_CATEGORIES.map((cat) => (
                                <button
                                    key={cat}
                                    data-testid={`gallery-filter-${cat.toLowerCase()}`}
                                    onClick={() => setFilter(cat)}
                                    className={`rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-[background-color,color,transform] duration-300 ${
                                        filter === cat
                                            ? "bg-ink text-gold"
                                            : "bg-white text-ink/55 hover:-translate-y-0.5 hover:text-ink"
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </FadeIn>
                </div>

                <motion.div layout className="columns-1 gap-5 sm:columns-2 lg:columns-3">
                    <AnimatePresence mode="popLayout">
                        {items.map((g) => (
                            <motion.figure
                                layout
                                key={g.id}
                                initial={{ opacity: 0, scale: 0.94 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.94 }}
                                transition={{ duration: 0.45, ease: EASE }}
                                className="group relative mb-5 break-inside-avoid overflow-hidden rounded-3xl shadow-sm"
                            >
                                <button
                                    data-testid={`gallery-item-${g.id}`}
                                    onClick={() => setSelected(g)}
                                    className="block w-full"
                                    aria-label={`Powiększ: ${g.title}`}
                                >
                                    <img
                                        src={g.src}
                                        alt={g.title}
                                        loading="lazy"
                                        className={`${g.aspect} w-full object-cover transition-transform duration-700 group-hover:scale-105`}
                                    />
                                    <span className="absolute inset-0 flex items-end justify-between bg-gradient-to-t from-ink/70 via-ink/0 to-ink/0 p-6 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                                        <span className="text-left">
                                            <span className="block text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">{g.cat}</span>
                                            <span className="mt-1 block font-display text-lg font-bold text-white">{g.title}</span>
                                        </span>
                                        <span className="rounded-full bg-white/15 p-2.5 text-white backdrop-blur-md">
                                            <Expand className="h-4 w-4" />
                                        </span>
                                    </span>
                                </button>
                            </motion.figure>
                        ))}
                    </AnimatePresence>
                </motion.div>
                {items.length === 0 && (
                    <p data-testid="gallery-empty" className="py-16 text-center text-sm text-ink/40">
                        Zdjęcia z realizacji już wkrótce.
                    </p>
                )}
                <BeforeAfter />
            </div>

            <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
                <DialogContent
                    data-testid="gallery-lightbox"
                    className="max-w-4xl border-0 bg-transparent p-0 shadow-none"
                >
                    <DialogTitle className="sr-only">{selected?.title}</DialogTitle>
                    {selected && (
                        <div className="overflow-hidden rounded-3xl">
                            <img
                                src={selected.src.includes("w=900") ? selected.src.replace("w=900", "w=1600") : selected.src}
                                alt={selected.title}
                                className="max-h-[80vh] w-full object-cover"
                            />
                            <div className="flex items-center justify-between bg-ink px-6 py-4">
                                <p className="font-display font-bold text-white">{selected.title}</p>
                                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">{selected.cat}</p>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </section>
    );
}
